import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemIcon, SystemIconNames } from '@monorepo/nova-icons';
import { Stack, Typography } from '@monorepo/nova-ui';
import { FooterIcon, LanguageOption } from '@monorepo/types';
import { useGetEnvironment } from '../../../hooks';
import {
  StyledIconButton,
  StyledIconGroup,
  StyledIconLink,
  StyledIconsRow,
  StyledSocialIconsContainer,
} from './styles/footer-socials.styled';

type Props = {
  icons: FooterIcon[];
};

type IconSectionProps = {
  icons: FooterIcon[];
  title?: string;
  currentLanguage: string;
};

// Render a single icon button
const renderIconButton = (icon: FooterIcon, currentLanguage: string) => {
  const href =
    icon.languages?.[currentLanguage] && icon.languages[currentLanguage] !== ''
      ? icon.languages[currentLanguage]
      : icon.href;

  return (
    <StyledIconLink
      key={icon.id}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={icon.name || icon.iconName}
    >
      <StyledIconButton>
        <SystemIcon iconName={icon.iconName as SystemIconNames} />
      </StyledIconButton>
    </StyledIconLink>
  );
};

// Render icon section with title
const IconSection: FunctionComponent<IconSectionProps> = ({
  icons,
  title,
  currentLanguage,
}) => {
  return (
    <StyledIconGroup>
      {title && <Typography variant="body2">{title}</Typography>}
      <StyledIconsRow>
        {icons
          .filter((icon) => icon.active)
          .map((icon) => renderIconButton(icon, currentLanguage))}
      </StyledIconsRow>
    </StyledIconGroup>
  );
};

export const RenderSocialIcons: FunctionComponent<Props> = ({ icons }) => {
  const { i18n, t } = useTranslation('hubCommon');
  const currentLanguage = i18n.resolvedLanguage ?? LanguageOption.ENGLISH;

  const { is5ers } = useGetEnvironment();
  const projectIcons = icons.filter((icon) => !icon.type);
  const futuresIcons = is5ers
    ? icons.filter((icon) => icon.type === 'futures')
    : [];

  return (
    <StyledSocialIconsContainer>
      {is5ers ? (
        <>
          {/* The5ers Icons */}
          <IconSection
            icons={projectIcons}
            title={t('footer.asset_class.the5ers', 'The5ers')}
            currentLanguage={currentLanguage}
          />

          {/* Futures Icons */}
          {futuresIcons && futuresIcons.length > 0 && (
            <IconSection
              icons={futuresIcons}
              title={t('footer.asset_class.futures', 'Futures')}
              currentLanguage={currentLanguage}
            />
          )}
        </>
      ) : (
        <Stack>
          {/* TTP Icons */}
          <IconSection icons={projectIcons} currentLanguage={currentLanguage} />
        </Stack>
      )}
    </StyledSocialIconsContainer>
  );
};
